/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/

import * as React from "react";
import { navigate } from "gatsby";
import { makeStyles } from "tss-react/mui";
import {
  DialogueMessageType,
  Goal,
  Lesson,
  LessonDisplayType,
  StudyCohort,
  StudyCohortTeam,
  Topic,
} from "@ictlearningsciences/pal3-core/types";
import {
  BookmarkBorderOutlined,
  BookmarkOutlined,
  History,
  FastForward,
  Edit,
} from "@mui/icons-material";
import { Card, CardContent, Grid, IconButton, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { useSpring, animated } from "@react-spring/web";

import { OutlinedButton, StartButton, TextButton } from "./buttons";
import { buildClassName } from "../helpers";
import { useWithWindow } from "../hooks/useWithWindow";
import { LessonResourceType } from "./lesson-card";
import { GoalMasteryIcon, TopicMasteryIcon } from "./mastery-icon";
import { useWithRecommender } from "../hooks/useWithRecommender";
import { useWithUserGoal } from "../hooks/useWithUserGoal";
import { useWithDialogue } from "../hooks/useWithDialogue";
import { SelfTestButton } from "./self-test";
import { useAppSelector } from "../store/hooks";
import { TooltipOverlay } from "./tooltip";

import card_side_button_2 from "../static/card_side_button_2.png";
import card_side_button_3 from "../static/card_side_button_3.png";

const useStyles = makeStyles()(() => ({
  cardTransparent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifySelf: "center",
    padding: 20,
    borderRadius: 10,
    backgroundColor: "rgba(245, 245, 245, 0.75)",
    backdropFilter: "blur(5px)",
    boxShadow:
      "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
  },
  cardGroup: {
    backgroundColor: "rgba(245, 245, 245, 0.95)",
    backdropFilter: "blur(5px)",
    boxShadow:
      "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
    borderRadius: 5,
    "&.active": {
      backgroundColor: "rgba(156, 212, 255, 0.95)",
    },
  },
  cardGroupHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignContent: "center",
    paddingTop: 20,
    paddingBottom: 10,
    paddingLeft: 20,
    paddingRight: 20,
    "&.sticky": {
      position: "sticky",
      top: 0,
      zIndex: 999,
      borderTopRightRadius: 5,
      borderTopLeftRadius: 5,
      backgroundColor: "rgba(245, 245, 245, 0.95)",
      boxShadow:
        "0 1px 1px 0 rgba(0, 0, 0, 0.1), 0 1px 1px 0 rgba(0, 0, 0, 0.1)",
    },
    "&.active": {
      backgroundColor: "rgba(156, 212, 255, 0.95)",
    },
  },
  card: {
    display: "flex",
    flexDirection: "row",
    backgroundColor: "#ffffff",
    borderRadius: 16,
    "&:not(:last-child)": {
      marginBottom: 20,
    },
    "&.active": {
      backgroundColor: "#D2EBFE",
    },
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    alignItems: "flex-start",
    textDecoration: "none !important",
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 5,
  },
  text: {
    fontSize: 16,
    fontWeight: 200,
    marginBottom: 10,
  },
  sideBar: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
  },
  animDiv: {
    width: "100%",
  },
  sideButton: {
    display: "flex",
    position: "absolute",
    top: 0,
    bottom: 0,
    right: 0,
    width: 35,
    alignItems: "center",
    backgroundColor: "#65afe9",
    borderTopRightRadius: 16,
    borderBottomRightRadius: 16,
  },
}));

function CardSideButton(props: {
  onClick: () => void;
  isTopic?: boolean;
}): JSX.Element {
  return (
    <div
      data-cy="card-side-btn"
      style={{
        width: 35,
        backgroundColor: "#65afe9",
      }}
      onClick={props.onClick}
    >
      <div
        style={{
          height: "100%",
          borderTopRightRadius: 16,
          borderBottomRightRadius: 16,
          backgroundImage: props.isTopic
            ? `url(${card_side_button_2})`
            : `url(${card_side_button_3})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }}
      ></div>
    </div>
  );
}

export function TransparentCard(props: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  style: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any;
}): JSX.Element {
  const { classes } = useStyles();
  return (
    <div className={classes.cardTransparent} style={props.style}>
      {props.children}
    </div>
  );
}

export function CardGroup(props: {
  title: string;
  name?: string;
  active?: boolean;
  stickyHeader?: boolean;
  headerButton?: JSX.Element;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children?: any;
}): JSX.Element {
  const { classes } = useStyles();

  return (
    <div
      data-cy={`card-group-${props.name || props.title}`.toLowerCase()}
      className={buildClassName(
        classes.cardGroup,
        props.active ? "active" : ""
      )}
    >
      {props.title ? (
        <div
          className={buildClassName(
            classes.cardGroupHeader,
            props.stickyHeader ? "sticky" : "",
            props.active ? "active" : ""
          )}
        >
          <div
            data-cy="card-group-title"
            className={classes.title}
            style={{ flexGrow: 1 }}
          >
            {props.title}
          </div>
          <div style={{ display: props.headerButton ? "flex" : "none" }}>
            {props.headerButton}
          </div>
        </div>
      ) : null}

      <div style={{ padding: 20, paddingTop: 0 }}>{props.children}</div>
    </div>
  );
}

export function CardComponent(props: {
  name?: string;
  active?: boolean;
  sideButton?: JSX.Element;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  style?: any;
}): JSX.Element {
  const { classes } = useStyles();
  const [cardAnim] = useSpring(
    () => ({
      from: { opacity: !props.active ? 0 : 1 },
      to: { opacity: props.active ? 1 : 1 },
      config: { mass: 3, friction: 5, tension: 70 },
    }),
    []
  );

  return (
    <Card
      data-cy={`card-${props.name}`.toLowerCase().replace(" ", "-")}
      style={props.style}
      className={(props.active
        ? [classes.card, "active"]
        : [classes.card]
      ).join(" ")}
    >
      <CardContent className={classes.cardContent}>
        <animated.div style={cardAnim} className={classes.animDiv}>
          {props.children}
        </animated.div>
      </CardContent>
      <div style={{ height: "auto" }}>{props.sideButton}</div>
    </Card>
  );
}

export function MyGoalCard(props: {
  goal: Goal | undefined;
  active?: boolean;
  includeSideButton?: boolean;
}): JSX.Element {
  const { goal } = props;
  const { classes } = useStyles();
  const { user } = useAppSelector((state) => state.userData);
  const [cohort, setCohort] = React.useState<StudyCohort>();
  const [team, setTeam] = React.useState<StudyCohortTeam>();

  React.useEffect(() => {
    if (user && goal) {
      const cohort = user.cohorts.find((c) => c.goal === goal.id);
      setCohort(cohort);
      if (cohort) {
        const idx = cohort.members.find((m) => m.user === user.id)?.teamIndex;
        if (idx) {
          setTeam(cohort.teams[idx]);
        }
      }
    }
  }, [user, goal]);

  return (
    <CardComponent
      name="mygoal"
      active={props.active}
      sideButton={
        props.includeSideButton ? (
          <div
            style={{ display: "flex", flexDirection: "row", height: "100%" }}
          >
            <div className={classes.sideBar}>
              <IconButton
                aria-label="edit goal button"
                onClick={() => navigate("/set-goal")}
              >
                <Edit style={{ color: "#5CB1D4" }} />
              </IconButton>
              <SelfTestButton goal={goal} />
            </div>
            <CardSideButton onClick={() => navigate("/set-goal")} />
          </div>
        ) : undefined
      }
    >
      {goal ? (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            verticalAlign: "middle",
          }}
        >
          <GoalMasteryIcon goal={goal} />
          <Grid container alignContent={"center"}>
            <Typography
              style={{ fontSize: "20px", fontWeight: 600, marginLeft: 5 }}
            >
              {goal.name}
            </Typography>
            {cohort && (
              <Typography style={{ fontSize: "18px", marginLeft: 5 }}>
                - Class {cohort.name} ({team?.name || "No Team"})
              </Typography>
            )}
          </Grid>
        </div>
      ) : (
        <Typography>Set your goal</Typography>
      )}
    </CardComponent>
  );
}

export function TopicCard(props: {
  topic: Topic | undefined;
  active?: boolean;
  hasBookmarked?: void;
}): JSX.Element {
  const { topic } = props;
  const { classes } = useStyles();
  const { activeTopic } = useAppSelector((state) => state.userData);
  const { setTopic } = useWithUserGoal();
  const { playMessage, getRandomMessage } = useWithDialogue();
  const [expand, setExpand] = React.useState<boolean>(false);

  function onTopicSelected(): void {
    if (!topic || topic.alias === activeTopic?.alias) return;
    setTopic(topic);
    playMessage(getRandomMessage(DialogueMessageType.TopicSet));
  }

  if (!topic) {
    return (
      <CardComponent name="topic" active={props.active}>
        <Typography style={{ marginLeft: 5 }}>
          You do not have a topic yet
        </Typography>
      </CardComponent>
    );
  }

  return (
    <CardComponent
      name="topic"
      active={props.active}
      sideButton={
        <div
          style={{ display: "flex", flexDirection: "row", height: "100%" }}
          className="topic-card"
        >
          <div className={classes.sideBar}>
            <IconButton
              aria-label={"set topic " + topic.name + " button"}
              data-cy="bookmark-btn"
              onClick={() => {
                onTopicSelected();
              }}
            >
              {props.active ? (
                <BookmarkOutlined style={{ color: "#5CB1D4" }} />
              ) : (
                <BookmarkBorderOutlined />
              )}
            </IconButton>
            <SelfTestButton topic={topic} />
          </div>
          <CardSideButton
            isTopic={true}
            onClick={() => {
              navigate(`/lessons?id=${topic?.id}`);
            }}
          />
        </div>
      }
    >
      <div
        style={{ display: "flex", flexDirection: "row" }}
        onClick={() => setExpand((prevState) => !prevState)}
      >
        <TopicMasteryIcon topic={topic} />
        <div style={{ marginLeft: 10, flex: 1, flexGrow: 1 }}>
          <div data-cy="title" className={classes.title}>
            {topic.name}
          </div>
          <div
            data-cy="text"
            className={classes.text}
            style={
              expand
                ? {}
                : {
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                  }
            }
          >
            {topic.desc}
          </div>
          <div
            style={{
              marginTop: 10,
              display:
                topic.prerequisiteTopics.length > 0 && expand ? "flex" : "none",
              flexDirection: "column",
            }}
          >
            <div className={classes.subtitle}>
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                style={{ color: "#ebd300" }}
              />{" "}
              Recommended Prerequisite(s)
            </div>
            {topic.prerequisiteTopics.map((pt) => (
              <div data-cy="prereq" key={pt.id} className={classes.text}>
                {pt.name}
              </div>
            ))}
          </div>
        </div>
      </div>
    </CardComponent>
  );
}

export function StudyCard(): JSX.Element {
  const { classes } = useStyles();
  const { activeTopic, records } = useAppSelector((state) => state.userData);
  const { asyncGetRecommendedLessons } = useWithRecommender();
  const { isMobile } = useWithWindow();
  const [lessonDesc, showLessonDesc] = React.useState<boolean>(false);
  const [recommendedLesson, setRecommendedLesson] = React.useState<Lesson>();

  React.useEffect(() => {
    if (!activeTopic || records.length === 0) {
      return;
    }
    asyncGetRecommendedLessons(activeTopic).then((d) => {
      setRecommendedLesson(d[0]);
    });
  }, [records, activeTopic]);

  async function skipLesson() {
    if (!recommendedLesson || !activeTopic) return;
    const lessons = await asyncGetRecommendedLessons(activeTopic);
    const idx = lessons.findIndex((l) => l.alias === recommendedLesson?.alias);
    if (idx === -1 || idx === lessons.length - 1) {
      setRecommendedLesson(lessons[0]);
    } else {
      setRecommendedLesson(lessons[idx + 1]);
    }
  }

  if (!activeTopic) {
    return <div />;
  }

  if (isMobile) {
    return (
      <div style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: 15,
            marginRight: 15,
          }}
        >
          <Typography style={{ fontStyle: "italic", fontSize: 14 }}>
            Study
          </Typography>
          <LessonResourceType
            type={recommendedLesson?.displayType || LessonDisplayType.PASSIVE}
          />
          <div style={{ flexGrow: 1 }} />
          <TextButton
            aria="view lesson history button"
            style={{ width: 50, marginRight: 10 }}
            onClick={() => navigate("/history")}
          >
            History
          </TextButton>
          <TextButton
            aria="skip lesson button"
            style={{ width: 50 }}
            onClick={skipLesson}
          >
            Skip
          </TextButton>
        </div>
        <Card
          style={{
            display: "flex",
            marginLeft: 10,
            marginRight: 10,
            padding: 10,
            borderRadius: 16,
          }}
        >
          <div style={{ flexGrow: 1 }}>
            <div
              style={{
                fontWeight: "bold",
                fontSize: 16,
                flex: 1,
                flexGrow: 1,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
              }}
            >
              {activeTopic !== undefined ? activeTopic.name : ""}
            </div>
            <div
              style={{
                fontSize: 12,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
              }}
            >
              {recommendedLesson?.name}
            </div>
          </div>
          <StartButton lesson={recommendedLesson?.id} />
        </Card>
      </div>
    );
  }

  return (
    <TooltipOverlay id="my-goal-4">
      <CardGroup title="Start Studying!" name="study" active>
        <CardComponent name="study">
          <div
            onClick={() => {
              showLessonDesc(!lessonDesc);
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <TopicMasteryIcon topic={activeTopic} />
              <div className={classes.title} style={{ marginLeft: 5 }}>
                {activeTopic !== undefined ? activeTopic.name : ""}
              </div>
            </div>
            <div style={{ marginLeft: 10, flex: 1, flexGrow: 1 }}>
              <div className={classes.subtitle}>{recommendedLesson?.name}</div>
              <div
                className={classes.text}
                style={{ display: lessonDesc ? "flex" : "none" }}
              >
                {recommendedLesson?.desc}
              </div>
              <div style={{ float: "right" }}>
                <LessonResourceType
                  type={
                    recommendedLesson !== undefined
                      ? recommendedLesson?.displayType
                      : LessonDisplayType.PASSIVE
                  }
                />
              </div>
            </div>
          </div>
        </CardComponent>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "space-evenly",
          }}
        >
          <OutlinedButton
            aria="view history button"
            endIcon={<History />}
            onClick={() => navigate("/history")}
          >
            History
          </OutlinedButton>
          <OutlinedButton
            aria="skip lesson button"
            endIcon={<FastForward />}
            onClick={skipLesson}
          >
            Skip
          </OutlinedButton>
          <StartButton lesson={recommendedLesson?.id} />
        </div>
      </CardGroup>
    </TooltipOverlay>
  );
}
